import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Pin, Plus, Search, Star, Users } from 'lucide-react'
import { waApi, type WaChat, type WaSession } from '../../api/whatsapp'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { fullName } from '../../lib/utils'
import { stripWhatsAppMarkers } from '../../lib/whatsappText'

// WhatsApp profile-picture URLs point at pps.whatsapp.net and EXPIRE (the `oe=`
// param). The CRM only refreshes them when a chat gets a new message, so avatars
// on quiet chats go stale and render as a broken image. Fall back to the initials
// placeholder when the image fails to load instead of showing a broken icon.
function ChatAvatar({ chat }: { chat: WaChat }) {
  const [failed, setFailed] = useState(false)
  if (chat.profilePicUrl && !failed) {
    return (
      <img
        src={chat.profilePicUrl}
        alt=""
        onError={() => setFailed(true)}
        className="h-10 w-10 shrink-0 rounded-full object-cover"
      />
    )
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600">
      {chat.isGroup ? <Users size={18} /> : (chatName(chat)[0]?.toUpperCase() ?? '?')}
    </div>
  )
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'favorites', label: 'Favorites' },
  { value: 'unlinked', label: 'Unlinked' },
  { value: 'archived', label: 'Archived' },
] as const

export interface ChatListColumnProps {
  companyId: string
  sessions: WaSession[]
  sessionId: string
  onSessionChange: (id: string) => void
  activeChatId: string | null
  onSelect: (id: string) => void
  onNewChat: () => void
}

export function ChatListColumn({
  companyId,
  sessions,
  sessionId,
  onSessionChange,
  activeChatId,
  onSelect,
  onNewChat,
}: ChatListColumnProps) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['value']>('all')

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['wa-chats', companyId, sessionId, search, filter],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      waApi.listChats(companyId, {
        sessionId: sessionId || undefined,
        search: search || undefined,
        archived: filter === 'archived' || undefined,
        unlinked: filter === 'unlinked' || undefined,
        favorites: filter === 'favorites' || undefined,
        // Unread is filtered server-side: done here it would only ever match the pages
        // already loaded, hiding older unread chats however far you scrolled.
        unread: filter === 'unread' || undefined,
        page: pageParam,
      }),
    getNextPageParam: last => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
    refetchInterval: 8_000,
  })

  // Dedupe by id: pages are offset-based over a list that reorders as messages arrive, so
  // a chat can briefly straddle a page boundary and arrive twice — which would otherwise
  // collide on the React key below. Map keeps first-seen order.
  const chats = [...new Map((data?.pages.flatMap(p => p.data) ?? []).map(c => [c.id, c])).values()]

  // Auto-load the next page when the sentinel scrolls into view.
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage) return
    const obs = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage() },
      { rootMargin: '120px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="flex w-80 shrink-0 flex-col border-r border-gray-100">
      <div className="space-y-2 border-b border-gray-100 p-3">
        <div className="flex items-center gap-2">
          {sessions.length > 1 && (
            <DropdownSelect
              className="min-w-0 flex-1"
              value={sessionId}
              onChange={onSessionChange}
              options={[
                { value: '', label: 'All numbers' },
                ...sessions.map(s => ({
                  value: s.id,
                  label: `${s.name}${s.phoneNumber ? ` (${s.phoneNumber})` : ''}`,
                })),
              ]}
            />
          )}
          <button className="btn btn-primary btn-sm ml-auto" onClick={onNewChat}>
            <Plus size={16} /> New
          </button>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
          <input
            className="input pl-7"
            placeholder="Search chats…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="flex gap-1">
          {FILTERS.map(f => (
            <button
              key={f.value}
              className={`btn btn-sm ${filter === f.value ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {chats.map(c => (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            className={`flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left transition-colors hover:bg-gray-50 ${
              activeChatId === c.id ? 'bg-gray-100' : ''
            }`}
          >
            <ChatAvatar key={c.profilePicUrl ?? 'ph'} chat={c} />


            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1 truncate font-medium">
                  {c.isPinned && <Pin size={11} className="shrink-0 fill-current text-green-600" />}
                  {c.isFavorite && <Star size={11} className="shrink-0 fill-current text-amber-400" />}
                  {chatName(c)}
                </span>
                {c.lastMessageAt && (
                  <span className="shrink-0 text-xs text-gray-400">{formatChatTime(c.lastMessageAt)}</span>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm text-gray-500">
                  {c.lastMessagePreview ? stripWhatsAppMarkers(c.lastMessagePreview) : ''}
                </span>
                {c.unreadCount > 0 ? (
                  <span className="ml-1 shrink-0 rounded-full bg-green-500 px-1.5 text-xs text-white">
                    {c.unreadCount}
                  </span>
                ) : c.manualUnread ? (
                  <span className="ml-1 h-2.5 w-2.5 shrink-0 rounded-full bg-green-500" title="Marked as unread" />
                ) : null}
              </div>

              {(c.labels.length > 0 || (!c.leadId && !c.isGroup)) && (
                <div className="flex items-center gap-1.5">
                  {c.labels.map(label => (
                    <span
                      key={label}
                      title={label}
                      className="h-2 w-2 shrink-0 rounded-full bg-blue-400"
                    />
                  ))}
                  {!c.leadId && !c.isGroup && (
                    <span className="text-xs text-amber-600">Not linked to a lead</span>
                  )}
                </div>
              )}
            </div>
          </button>
        ))}

        {hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center py-3">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="btn btn-ghost btn-sm text-gray-500"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}

        {chats.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-400">No chats</div>
        )}
      </div>
    </div>
  )
}

export function chatName(c: Pick<WaChat, 'lead' | 'displayName' | 'number' | 'isGroup'>) {
  // A group's name is always its WhatsApp subject (displayName) — never the linked
  // lead's name. The lead link is separate CRM context, not the group's identity.
  if (c.isGroup) return c.displayName || 'Group'
  // Facebook leads have only a full name in firstName (lastName null) — fullName()
  // drops the null instead of rendering "Name null".
  if (c.lead) return fullName(c.lead.firstName, c.lead.lastName) || c.displayName || c.number || 'Unknown'
  return c.displayName || c.number || 'Unknown'
}

function formatChatTime(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString()
}
